import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";
import UserDetailModal from "./UserDetailModal";
import { Softphone } from "@frejun/softphone-web-sdk";

const STATUS_OPTIONS = [
  "new_lead",
  "pending",
  "called",
  "follow_up",
  "reached",
  "not_interested",
  "rejected",
  "forwarded",
  "account_made",
  "closed",
  "underage",
  "ftd",
  "call_back",
  "no_answer",
  "no_language",
];

const PAGE_SIZE_OPTIONS = [40, 50, 100, 200];

const GroxionLead = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { allStaff, adminUser } = useSelector((state) => state.admin);
  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState([]);
  const [activeSource, setActiveSource] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [statusInputs, setStatusInputs] = useState({});
  const [customStatus, setCustomStatus] = useState({});
  const [updating, setUpdating] = useState({});
  const [expandedLead, setExpandedLead] = useState(null);
  const [passwordInputs, setPasswordInputs] = useState({});
  const [showPasswordField, setShowPasswordField] = useState({});
  const [converting, setConverting] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(40);
  const [statusFilter, setStatusFilter] = useState("all");
  const [staffFilter, setStaffFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [depositFilter, setDepositFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [settingLeadType, setSettingLeadType] = useState({});

  // Confirm-move modal state
  const [confirmMove, setConfirmMove] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Phone management state
  const [newPhoneInputs, setNewPhoneInputs] = useState({});
  const [newPhoneLabels, setNewPhoneLabels] = useState({});
  const [addingPhone, setAddingPhone] = useState({});
  const [deletingPhone, setDeletingPhone] = useState({});
  const [settingPrimaryPhone, setSettingPrimaryPhone] = useState({});
  const [copiedPhone, setCopiedPhone] = useState({});

  // Bulk assign state
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkAssignStatus, setBulkAssignStatus] = useState("no_answer");
  const [selectedBulkStaff, setSelectedBulkStaff] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssignCount, setBulkAssignCount] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // ─── FREJUN SOFT PHONE STATE ───
  const [isCallActive, setIsCallActive] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null); // { lead } — number never stored
  const softphoneRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localAudioRef = useRef(null);
  const incomingTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);

  // ─── CALL HISTORY STATE ───
  const [callHistory, setCallHistory] = useState([]);
  const [showCallHistory, setShowCallHistory] = useState(false);

  const FREJUN_TOKEN = import.meta.env.VITE_FREJUN_TOKEN;
  const FREJUN_EMAIL =
    adminUser?.frejunEmail ||
    import.meta.env.VITE_FREJUN_EMAIL ||
    "finance@groxion.com";
  const AGENT_VIRTUAL_NUMBER = adminUser?.frejunNumber || null;

  // Strict masking logic
  const canSeeFullDetails = () => {
    if (!adminUser) return false;
    return adminUser.role === "admin" || adminUser.role === "super_admin";
  };

  const conversionAssignable = allStaff.filter(
    (s) =>
      s.role !== "super_admin" &&
      (s.staffType || "conversion") === "conversion",
  );

  useEffect(() => {
    fetchLeads();
  }, [activeSource]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds(new Set());
    setSelectAll(false);
  }, [
    activeSource,
    statusFilter,
    staffFilter,
    dateFilter,
    depositFilter,
    searchResult,
    pageSize,
  ]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (confirmMove && !confirmLoading) setConfirmMove(null);
        if (showCallHistory) setShowCallHistory(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmMove, confirmLoading, showCallHistory]);

  // ─── RINGTONE SETUP & UNLOCK ───
  useEffect(() => {
    ringtoneRef.current = new Audio("/the_fire_love.mp3");
    ringtoneRef.current.loop = true;
    ringtoneRef.current.preload = "auto";

    const unlockAudio = () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.volume = 0;
        ringtoneRef.current.play()
          .then(() => {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
            ringtoneRef.current.volume = 1;
            console.log("[Audio] Ringtone unlocked successfully");
          })
          .catch((e) => console.warn("[Audio] Unlock blocked by browser:", e));
      }
      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  const playRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((e) => {
        console.error("[Audio] Play failed:", e);
        dispatch(showToast({ message: "Please click anywhere on the screen to allow ringtone audio.", type: "warning" }));
      });
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const clearIncoming = () => {
    if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
    incomingTimeoutRef.current = null;
    stopRingtone();
    setIncomingCall(null);
  };

  // ─── INIT FREJUN SOFT PHONE ───
  useEffect(() => {
    if (!FREJUN_TOKEN || !adminUser?.frejunEmail) return;

    let isMounted = true;
    const initSoftphone = async () => {
      try {
        const softphone = new Softphone();
        await softphone.login({ type: "OAuth2.0", token: FREJUN_TOKEN, email: FREJUN_EMAIL });
        if (!isMounted) return;
        softphoneRef.current = softphone;

        const listeners = {
          onConnectionStateChange: (type, newState) => {},
          onCallCreated: (type, details) => {
            if (type === "Incoming") {
              setIncomingCall({ lead: null });
              playRingtone();

              // Grab the number directly from the raw SIP Session
              let callerNumber = details?.candidate || details?.request?.from?.uri?.user || "";
              
              if (callerNumber.includes("sip:")) {
                  callerNumber = callerNumber.split("sip:")[1].split("@")[0];
              }

              console.log("Extracted Caller Number:", callerNumber);

              const historyId = Date.now();
              setCallHistory((prev) => [
                {
                  id: historyId,
                  leadName: "Unknown Caller",
                  shortId: null,
                  timestamp: new Date().toLocaleString(),
                  status: "Unknown",
                },
                ...prev.slice(0, 19),
              ]);

              if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
              incomingTimeoutRef.current = setTimeout(() => setIncomingCall(null), 60000);

              // Lookup lead by number
              if (callerNumber) {
                adminApi.post("/leads/lookup-phone", { phone: callerNumber })
                  .then((res) => {
                    console.log("Lookup Response:", res.data);
                    if (res.data?.found) {
                      setIncomingCall((prev) => (prev ? { lead: res.data.lead } : prev));
                      setCallHistory((prev) =>
                        prev.map((h) =>
                          h.id === historyId
                            ? { ...h, leadName: res.data.lead.name, shortId: res.data.lead.short_id, status: "Lead Matched" }
                            : h
                        )
                      );
                    }
                  })
                  .catch((err) => console.error("Lookup Failed:", err));
              }
            } else if (type === "Outgoing") {
              setIsCallActive(true);
            }
          },
          onCallRinging: () => {},
          onCallHangup: () => {
            setIsCallActive(false);
            clearIncoming();
          },
        };

        const audioElements = { remote: remoteAudioRef.current, local: localAudioRef.current };
        await softphone.start(listeners, audioElements, { endSessionOnNavigate: false, logoutOnNavigate: false });
      } catch (err) {
        console.error("[Softphone] init failed:", err);
        if (isMounted) dispatch(showToast({ message: "Dialer init failed. Check token/email.", type: "error" }));
      }
    };

    initSoftphone();
    return () => {
      isMounted = false;
      softphoneRef.current?.logout();
      softphoneRef.current = null;
      stopRingtone();
    };
  }, [FREJUN_TOKEN, FREJUN_EMAIL, adminUser?.frejunEmail, dispatch]);

  const handleFrejunCall = async (phoneNumber, leadName) => {
    if (!phoneNumber || !softphoneRef.current) {
      dispatch(showToast({ message: "Dialer not ready. Please wait.", type: "error" }));
      return;
    }
    try {
      setIsCallActive(true);
      await softphoneRef.current.makeCall(phoneNumber, AGENT_VIRTUAL_NUMBER || undefined, {});
    } catch (err) {
      console.error("[Softphone] makeCall failed:", err);
      setIsCallActive(false);
      dispatch(showToast({ message: "Call failed.", type: "error" }));
    }
  };

  const acceptCall = async () => {
    stopRingtone();
    try {
      await softphoneRef.current?.getSession?.accept();
      setIncomingCall(null);
      setIsCallActive(true);
    } catch (err) {
      console.error("[Softphone] accept failed:", err);
      dispatch(showToast({ message: "Accept failed", type: "error" }));
    }
  };

  const declineCall = async () => {
    stopRingtone();
    clearIncoming();
    setIsCallActive(false);
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
  };

  const handleHangup = async () => {
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
    setIsCallActive(false);
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    setSearchResult(null);
    setSearchQuery("");
    try {
      const url =
        activeSource === "all"
          ? "/leads/groxion"
          : `/leads/groxion?source=${activeSource}`;
      const res = await adminApi.get(url);
      setLeads(res.data?.leads || []);
      if (res.data?.sources) setSources(res.data.sources);
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to load AnzoTrade leads", type: "error" }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      setSearchResult(null);
      return;
    }
    setIsSearching(true);
    setSearchResult(null);
    try {
      try {
        const res = await adminApi.get(`/leads/short/${query.toUpperCase()}`);
        if (res.data?.lead) {
          setSearchResult([res.data.lead]);
          setCurrentPage(1);
          setIsSearching(false);
          return;
        }
      } catch (e) {}
      const q = query.toLowerCase();
      const filtered = leads.filter(
        (lead) =>
          (lead.name && lead.name.toLowerCase().includes(q)) ||
          (lead.email && lead.email.toLowerCase().includes(q)) ||
          (lead.phone && lead.phone.toLowerCase().includes(q)) ||
          (lead.short_id && lead.short_id.toLowerCase().includes(q)),
      );
      setSearchResult(filtered);
      setCurrentPage(1);
    } catch (e) {
      dispatch(showToast({ message: "Search failed", type: "error" }));
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResult(null);
    setCurrentPage(1);
  };

  const getFilteredLeads = () => {
    let filtered = searchResult !== null ? searchResult : leads;
    if (statusFilter !== "all")
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    if (staffFilter !== "all") {
      if (staffFilter === "unassigned")
        filtered = filtered.filter(
          (lead) => !lead.assignedTo && !lead.teamLeadId,
        );
      else
        filtered = filtered.filter(
          (lead) =>
            lead.assignedTo === staffFilter || lead.teamLeadId === staffFilter,
        );
    }
    if (dateFilter) {
      const filterDate = new Date(dateFilter).toDateString();
      filtered = filtered.filter((lead) => {
        if (!lead.createdAt) return false;
        return new Date(lead.createdAt).toDateString() === filterDate;
      });
    }

    if (depositFilter !== "all") {
      filtered = filtered.filter((lead) => {
        if (lead.depositStatus !== "deposited") return false;
        const amount = Number(lead.depositAmount) || 0;
        if (depositFilter === "gte100") return amount >= 100;
        if (depositFilter === "gte500") return amount >= 500;
        if (depositFilter === "gte1000") return amount >= 1000;
        return true;
      });
    }

    return filtered;
  };

  const displayLeads = getFilteredLeads();
  const totalPages =
    pageSize === -1 ? 1 : Math.ceil(displayLeads.length / pageSize);
  const effectivePageSize = pageSize === -1 ? displayLeads.length : pageSize;
  const paginatedLeads =
    pageSize === -1
      ? displayLeads
      : displayLeads.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize,
        );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      const newSelected = new Set(selectedLeadIds);
      paginatedLeads.forEach((lead) => newSelected.delete(lead.id));
      setSelectedLeadIds(newSelected);
      setSelectAll(false);
    } else {
      const newSelected = new Set(selectedLeadIds);
      paginatedLeads.forEach((lead) => {
        if (
          lead.lead_type !== "retention" ||
          (!lead.assignedTo && !lead.teamLeadId)
        )
          newSelected.add(lead.id);
      });
      setSelectedLeadIds(newSelected);
      setSelectAll(true);
    }
  };

  const handleToggleLead = (leadId, isRetentionLocked) => {
    if (isRetentionLocked) return;
    const newSelected = new Set(selectedLeadIds);
    if (newSelected.has(leadId)) newSelected.delete(leadId);
    else newSelected.add(leadId);
    setSelectedLeadIds(newSelected);
    const allSelectable = paginatedLeads.filter(
      (l) => l.lead_type !== "retention" || (!l.assignedTo && !l.teamLeadId),
    );
    setSelectAll(
      allSelectable.length > 0 &&
        allSelectable.every((l) => newSelected.has(l.id)),
    );
  };

  const handleSelectAllFiltered = () => {
    const newSelected = new Set();
    displayLeads.forEach((lead) => {
      if (
        lead.lead_type !== "retention" ||
        (!lead.assignedTo && !lead.teamLeadId)
      )
        newSelected.add(lead.id);
    });
    setSelectedLeadIds(newSelected);
    setSelectAll(true);
  };

  const handleClearSelection = () => {
    setSelectedLeadIds(new Set());
    setSelectAll(false);
  };

  const copyPhoneToClipboard = (phone, phoneId) => {
    navigator.clipboard
      .writeText(phone)
      .then(() => {
        setCopiedPhone({ ...copiedPhone, [phoneId]: true });
        dispatch(
          showToast({
            message: `Copied: ${phone}`,
            type: "success",
            duration: 1500,
          }),
        );
        setTimeout(
          () => setCopiedPhone({ ...copiedPhone, [phoneId]: false }),
          1500,
        );
      })
      .catch(() => {});
  };

  const handleAddPhone = async (leadId) => {
    const phone = newPhoneInputs[leadId]?.trim();
    if (!phone) {
      dispatch(
        showToast({ message: "Phone number is required", type: "error" }),
      );
      return;
    }
    setAddingPhone({ ...addingPhone, [leadId]: true });
    try {
      const label = newPhoneLabels[leadId]?.trim() || null;
      const res = await adminApi.post(`/leads/${leadId}/phones`, {
        phone,
        label,
      });
      if (res.data?.phones)
        setLeads((prev) =>
          prev.map((l) =>
            l.id === leadId ? { ...l, phones: res.data.phones } : l,
          ),
        );
      dispatch(showToast({ message: "Phone added", type: "success" }));
      setNewPhoneInputs({ ...newPhoneInputs, [leadId]: "" });
      setNewPhoneLabels({ ...newPhoneLabels, [leadId]: "" });
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to add phone",
          type: "error",
        }),
      );
    } finally {
      setAddingPhone({ ...addingPhone, [leadId]: false });
    }
  };

  const handleRemovePhone = async (leadId, phoneId) => {
    if (!confirm("Remove this phone number?")) return;
    setDeletingPhone({ ...deletingPhone, [phoneId]: true });
    try {
      const res = await adminApi.delete(`/leads/${leadId}/phones/${phoneId}`);
      if (res.data?.phones)
        setLeads((prev) =>
          prev.map((l) =>
            l.id === leadId ? { ...l, phones: res.data.phones } : l,
          ),
        );
      dispatch(showToast({ message: "Phone removed", type: "success" }));
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to remove phone",
          type: "error",
        }),
      );
    } finally {
      setDeletingPhone({ ...deletingPhone, [phoneId]: false });
    }
  };

  const handleSetPrimaryPhone = async (leadId, phoneId) => {
    setSettingPrimaryPhone({ ...settingPrimaryPhone, [phoneId]: true });
    try {
      const res = await adminApi.put(
        `/leads/${leadId}/phones/${phoneId}/primary`,
      );
      if (res.data?.phones)
        setLeads((prev) =>
          prev.map((l) =>
            l.id === leadId ? { ...l, phones: res.data.phones } : l,
          ),
        );
      dispatch(
        showToast({ message: "Primary phone updated", type: "success" }),
      );
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to set primary",
          type: "error",
        }),
      );
    } finally {
      setSettingPrimaryPhone({ ...settingPrimaryPhone, [phoneId]: false });
    }
  };

  const handleAssign = async (leadId, staffId) => {
    if (!staffId) return;
    try {
      await adminApi.put(`/leads/${leadId}/assign`, { staffId });
      dispatch(showToast({ message: "Lead assigned", type: "success" }));
      fetchLeads();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to assign",
          type: "error",
        }),
      );
    }
  };

  const handleTransfer = async (leadId, staffId) => {
    if (!staffId) return;
    try {
      await adminApi.put(`/leads/${leadId}/transfer`, { staffId });
      dispatch(showToast({ message: "Lead transferred", type: "success" }));
      fetchLeads();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to transfer",
          type: "error",
        }),
      );
    }
  };

  const requestSetLeadType = (lead, targetType) => {
    if ((lead.lead_type || "conversion") === targetType) return;
    setConfirmMove({ leadId: lead.id, targetType });
  };

  const handleSetLeadType = async (leadId, leadType) => {
    setSettingLeadType({ ...settingLeadType, [leadId]: true });
    try {
      const res = await adminApi.put(`/leads/${leadId}/type`, { leadType });
      if (res.data?.lead)
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? res.data.lead : l)),
        );
      dispatch(
        showToast({ message: `Lead set to ${leadType}`, type: "success" }),
      );
      await fetchLeads();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to update lead type",
          type: "error",
        }),
      );
    } finally {
      setSettingLeadType({ ...settingLeadType, [leadId]: false });
    }
  };

  const confirmMoveLead = async () => {
    if (!confirmMove) return;
    setConfirmLoading(true);
    await handleSetLeadType(confirmMove.leadId, confirmMove.targetType);
    setConfirmLoading(false);
    setConfirmMove(null);
  };

  const handleUpdate = async (leadId) => {
    const comment = commentInputs[leadId] || "";
    const dropdownStatus = statusInputs[leadId] || "";
    const custom = customStatus[leadId] || "";
    const status = custom || dropdownStatus || null;
    if (!comment && !status) {
      dispatch(
        showToast({
          message: "Add a comment or select a status",
          type: "error",
        }),
      );
      return;
    }
    setUpdating({ ...updating, [leadId]: true });
    try {
      const res = await adminApi.put(`/leads/${leadId}/status`, {
        comment,
        status,
      });
      if (res.data?.lead)
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? res.data.lead : l)),
        );
      dispatch(showToast({ message: "Lead updated", type: "success" }));
      setCommentInputs({ ...commentInputs, [leadId]: "" });
      setStatusInputs({ ...statusInputs, [leadId]: "" });
      setCustomStatus({ ...customStatus, [leadId]: "" });
    } catch (e) {
      dispatch(showToast({ message: "Failed to update", type: "error" }));
    } finally {
      setUpdating({ ...updating, [leadId]: false });
    }
  };

  const handleDelete = async (leadId) => {
    if (!confirm("Delete this lead?")) return;
    try {
      await adminApi.delete(`/leads/${leadId}`);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      dispatch(showToast({ message: "Lead deleted", type: "success" }));
    } catch (e) {
      dispatch(showToast({ message: "Failed to delete", type: "error" }));
    }
  };

  // Triggers FreJun Dialer securely
  const handleDialNumber = async (leadId, e) => {
    e.stopPropagation();
    if (!AGENT_VIRTUAL_NUMBER) {
      dispatch(
        showToast({
          message: "No virtual number assigned to your account. Contact Admin.",
          type: "error",
        }),
      );
      return;
    }

    try {
      const res = await adminApi.get(`/leads/${leadId}/dial`);
      if (res.data?.phone) {
        const lead = leads.find((l) => l.id === leadId);
        const leadName = lead ? lead.name : "Unknown";
        handleFrejunCall(res.data.phone, leadName);
      }
    } catch (e) {
      dispatch(showToast({ message: "No phone number found", type: "error" }));
    }
  };

  const handleConvertToUser = async (leadId) => {
    const password = passwordInputs[leadId] || "";
    if (!password || password.length < 6) {
      dispatch(
        showToast({
          message: "Password must be at least 6 characters",
          type: "error",
        }),
      );
      return;
    }
    setConverting({ ...converting, [leadId]: true });
    try {
      const res = await adminApi.post(`/leads/${leadId}/convert`, { password });
      dispatch(
        showToast({
          message: `Account created! User ID: ${res.data.lead?.short_id}`,
          type: "success",
        }),
      );
      setPasswordInputs({ ...passwordInputs, [leadId]: "" });
      setShowPasswordField({ ...showPasswordField, [leadId]: false });
      fetchLeads();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to create account",
          type: "error",
        }),
      );
    } finally {
      setConverting({ ...converting, [leadId]: false });
    }
  };

  const handleResetPassword = async (leadId) => {
    const password = passwordInputs[leadId] || "";
    if (!password || password.length < 6) {
      dispatch(
        showToast({
          message: "Password must be at least 6 characters",
          type: "error",
        }),
      );
      return;
    }
    setConverting({ ...converting, [leadId]: true });
    try {
      await adminApi.put(`/leads/${leadId}/password`, { password });
      dispatch(
        showToast({
          message: "Password updated successfully",
          type: "success",
        }),
      );
      setPasswordInputs({ ...passwordInputs, [leadId]: "" });
      setShowPasswordField({ ...showPasswordField, [leadId]: false });
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to update password",
          type: "error",
        }),
      );
    } finally {
      setConverting({ ...converting, [leadId]: false });
    }
  };

  const handleViewLinkedUser = async (leadId) => {
    try {
      const res = await adminApi.get(`/leads/${leadId}/user`);
      if (res.data?.user) {
        setSelectedUser(res.data.user);
        setShowUserModal(true);
      }
    } catch (e) {
      dispatch(
        showToast({ message: "No user linked to this lead", type: "error" }),
      );
    }
  };

  const toggleBulkStaff = (staffId) => {
    setSelectedBulkStaff((prev) =>
      prev.includes(staffId)
        ? prev.filter((id) => id !== staffId)
        : [...prev, staffId],
    );
  };

  const getLeadsForBulkAssign = () => {
    if (selectedLeadIds.size > 0)
      return leads.filter((l) => selectedLeadIds.has(l.id));
    return leads.filter((l) => {
      if (l.status !== bulkAssignStatus) return false;
      if (l.lead_type === "retention" && (l.assignedTo || l.teamLeadId))
        return false;
      return true;
    });
  };

  const getBulkAssignLeadIds = () => {
    let availableLeads = getLeadsForBulkAssign();
    if (bulkAssignCount && Number(bulkAssignCount) > 0)
      availableLeads = availableLeads.slice(0, Number(bulkAssignCount));
    return availableLeads.map((l) => l.id);
  };

  const handleBulkAssign = async () => {
    if (selectedBulkStaff.length === 0) {
      dispatch(
        showToast({
          message: "Please select at least one person",
          type: "error",
        }),
      );
      return;
    }
    const leadIds = getBulkAssignLeadIds();
    if (leadIds.length === 0) {
      dispatch(
        showToast({ message: "No assignable leads found", type: "error" }),
      );
      return;
    }
    if (
      !confirm(
        `Assign ${leadIds.length} lead(s) to ${selectedBulkStaff.length} assignee(s)?`,
      )
    )
      return;

    setBulkAssigning(true);
    try {
      const res = await adminApi.post("/leads/bulk-assign", {
        status: bulkAssignStatus,
        staffIds: selectedBulkStaff,
        leadIds,
      });
      dispatch(showToast({ message: res.data.message, type: "success" }));
      setShowBulkAssign(false);
      setSelectedBulkStaff([]);
      setBulkAssignCount("");
      setSelectedLeadIds(new Set());
      setSelectAll(false);
      fetchLeads();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Bulk assign failed",
          type: "error",
        }),
      );
    } finally {
      setBulkAssigning(false);
    }
  };

  const getSourceBadge = (source) => {
    if (!source || source === "website")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-[#2563eb] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider">
          WEBSITE
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-[#7c3aed] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider">
        {source}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      new_lead: "bg-[#059669] text-white",
      pending: "bg-[#d97706] text-white",
      called: "bg-[#2563eb] text-white",
      follow_up: "bg-[#7c3aed] text-white",
      reached: "bg-[#0d9488] text-white",
      not_interested: "bg-[#64748b] text-white",
      rejected: "bg-[#dc2626] text-white",
      forwarded: "bg-[#1e3a5f] text-white",
      account_made: "bg-[#059669] text-white",
      underage: "bg-[#dc2626] text-white",
      ftd: "bg-[#0d9488] text-white",
      call_back: "bg-[#d97706] text-white",
      no_answer: "bg-[#d97706] text-white",
      no_language: "bg-[#ec4899] text-white",
      closed: "bg-[#64748b] text-white",
    };
    const displayName =
      status === "new_lead"
        ? "NEW LEAD"
        : status
          ? status.replace(/_/g, " ").toUpperCase()
          : "NEW LEAD";
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${colors[status] || "bg-[#94a3b8] text-white"} ${status === "new_lead" ? "border-[#047857]" : status === "pending" ? "border-[#b45309]" : "border-black/20"}`}
      >
        {displayName}
      </span>
    );
  };

  const getLeadTypeBadge = (leadType) => {
    if (leadType === "retention")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#fef3c7] text-[#92400e] text-[9px] font-mono font-extrabold border-2 border-[#fcd34d]">
          <i className="fas fa-lock text-[8px]" /> RETENTION
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[9px] font-mono font-extrabold border-2 border-[#a7f3d0]">
        <i className="fas fa-rotate text-[8px]" /> CONVERSION
      </span>
    );
  };

  const getGroxionBadge = () => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#0d9488] text-white text-[9px] font-mono font-extrabold border-2 border-[#0f766e]">
      <i className="fas fa-check-circle text-[8px]" /> AnzoTrade
    </span>
  );

  const getRoleLabel = (role) => {
    switch (role) {
      case "team_lead":
        return "TL";
      case "manager":
        return "MGR";
      case "admin":
        return "ADM";
      case "staff":
        return "STF";
      default:
        return "";
    }
  };

  const getDepositBadge = (lead) => {
    if (lead.depositStatus === "deposited") {
      const amount = lead.depositAmount
        ? `$${Number(lead.depositAmount).toFixed(0)}`
        : "";
      const dateTime = lead.depositDate
        ? new Date(lead.depositDate).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[9px] font-mono font-extrabold border-2 border-[#a7f3d0] flex-wrap"
          title={`Deposited: ${amount} on ${dateTime}`}
        >
          <i className="fas fa-check-circle text-[8px]" />
          <span className="font-extrabold">{amount}</span>
          {dateTime && (
            <span className="text-[7px] opacity-70 ml-0.5">{dateTime}</span>
          )}
        </span>
      );
    }
    return null;
  };

  const formatDate = (dateStr) =>
    !dateStr
      ? "—"
      : new Date(dateStr).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  const formatDateTime = (dateStr) =>
    !dateStr
      ? "—"
      : new Date(dateStr).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className="flex items-center justify-between px-5 py-3.5 border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
        <span className="text-[11px] text-[#475569] font-mono font-extrabold">
          Showing {(currentPage - 1) * effectivePageSize + 1}–
          {Math.min(currentPage * effectivePageSize, displayLeads.length)} of{" "}
          <span className="text-[#0f172a]">{displayLeads.length}</span> leads
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-angles-left text-[9px]" />
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chevron-left text-[9px]" />
          </button>
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`w-8 h-8 rounded-lg text-[11px] font-mono font-extrabold transition-all ${currentPage === page ? "bg-[#1e3a5f] text-white border-2 border-[#1e3a5f] shadow-md" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#1e3a5f] hover:border-[#1e3a5f]"}`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chevron-right text-[9px]" />
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-angles-right text-[9px]" />
          </button>
        </div>
      </div>
    );
  };

  if (isLoading)
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const selectedCount = selectedLeadIds.size;

  return (
    <div>
      <audio ref={remoteAudioRef} autoPlay />
      <audio ref={localAudioRef} autoPlay muted />

      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            AnzoTrade LEADS{" "}
            <span className="text-[#0d9488] text-sm">(ACTIVE)</span>
          </h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">
            {displayLeads.length} active lead
            {displayLeads.length !== 1 ? "s" : ""}{" "}
            {(statusFilter !== "all" ||
              staffFilter !== "all" ||
              dateFilter ||
              depositFilter !== "all") &&
              " (filtered)"}
            {selectedCount > 0 ? ` · ${selectedCount} selected` : ""}
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8] text-[11px]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value.trim()) {
                  setSearchResult(null);
                  setCurrentPage(1);
                }
              }}
              placeholder="Search by ID, name, email..."
              className="w-56 bg-white border-2 border-[#cbd5e1] rounded-xl py-2.5 pl-10 pr-10 text-[#0f172a] text-[11px] font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#dc2626] text-[11px]"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={isSearching || !searchQuery.trim()}
            className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[2px] border-[#0b1e33]"
          >
            {isSearching ? (
              <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "SEARCH"
            )}
          </button>
        </form>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBulkAssign(!showBulkAssign)}
            className="px-4 py-2 rounded-xl bg-[#7c3aed] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#6d28d9] transition-all border-b-[2px] border-[#6d28d9]"
          >
            <i className="fas fa-users-gear mr-1.5" /> BULK ASSIGN
          </button>
          <button
            onClick={fetchLeads}
            className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
          >
            <i className="fas fa-sync-alt mr-1.5" /> REFRESH
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 px-5 py-3 border-b-2 border-[#e2e8f0] overflow-x-auto items-center bg-white">
        <button
          onClick={() => {
            setActiveSource("all");
            clearSearch();
          }}
          className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold transition-all whitespace-nowrap uppercase tracking-wider ${activeSource === "all" ? "bg-[#0d9488] text-white shadow-md" : "bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#0d9488] hover:border-[#0d9488]"}`}
        >
          ALL AnzoTrade LEADS
        </button>
        {sources.map((src) => (
          <button
            key={src}
            onClick={() => {
              setActiveSource(src);
              clearSearch();
            }}
            className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold transition-all whitespace-nowrap uppercase tracking-wider ${activeSource === src ? "bg-[#0d9488] text-white shadow-md" : "bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#0d9488] hover:border-[#0d9488]"}`}
          >
            {src}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
          FILTERS:
        </span>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] transition-all cursor-pointer"
        >
          <option value="all">ALL STATUSES</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === "new_lead"
                ? "NEW LEAD"
                : s.replace(/_/g, " ").toUpperCase()}
            </option>
          ))}
        </select>
        <select
          value={staffFilter}
          onChange={(e) => setStaffFilter(e.target.value)}
          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] transition-all cursor-pointer"
        >
          <option value="all">ALL STAFF</option>
          <option value="unassigned">UNASSIGNED</option>
          {conversionAssignable.map((s) => (
            <option key={s.id} value={s.id}>
              {s.firstName} {s.lastName}
              {s.role !== "staff" ? ` (${getRoleLabel(s.role)})` : ""}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] transition-all"
        />
        <select
          value={depositFilter}
          onChange={(e) => setDepositFilter(e.target.value)}
          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] transition-all cursor-pointer"
        >
          <option value="all">ALL DEPOSITS</option>
          <option value="any">DEPOSITED (ANY)</option>
          <option value="gte100">DEPOSITED ≥ $100</option>
          <option value="gte500">DEPOSITED ≥ $500</option>
          <option value="gte1000">DEPOSITED ≥ $1000</option>
        </select>
        {(statusFilter !== "all" ||
          staffFilter !== "all" ||
          dateFilter ||
          depositFilter !== "all") && (
          <button
            onClick={() => {
              setStatusFilter("all");
              setStaffFilter("all");
              setDateFilter("");
              setDepositFilter("all");
            }}
            className="text-[10px] text-[#dc2626] font-mono font-extrabold hover:underline uppercase tracking-wider"
          >
            CLEAR FILTERS
          </button>
        )}
        <div className="w-px h-6 bg-[#e2e8f0]" />
        <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
          SHOW:
        </span>
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] transition-all cursor-pointer"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
          <option value={-1}>ALL</option>
        </select>
        <span className="ml-auto text-[10px] text-[#475569] font-mono font-extrabold">
          {displayLeads.length} LEAD{displayLeads.length !== 1 ? "S" : ""} FOUND
        </span>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-5 py-2 bg-[#f0fdfa] border-b-2 border-[#99f6e4]">
          <span className="text-[10px] text-[#0d9488] font-mono font-extrabold">
            {selectedCount} lead{selectedCount !== 1 ? "s" : ""} selected
          </span>
          <button
            onClick={handleSelectAllFiltered}
            className="text-[10px] text-[#0f766e] font-mono font-extrabold hover:underline"
          >
            Select all {displayLeads.length} filtered
          </button>
          <button
            onClick={handleClearSelection}
            className="text-[10px] text-[#dc2626] font-mono font-extrabold hover:underline"
          >
            Clear selection
          </button>
          <button
            onClick={() => setShowBulkAssign(true)}
            className="ml-auto px-4 py-1.5 rounded-lg bg-[#7c3aed] text-white text-[10px] font-mono font-extrabold hover:bg-[#6d28d9] transition-all border-b-[2px] border-[#6d28d9]"
          >
            <i className="fas fa-users-gear mr-1.5" /> BULK ASSIGN SELECTED
          </button>
        </div>
      )}

      {showBulkAssign && (
        <div className="mx-5 mt-4 p-5 bg-white border-2 border-[#0d9488] rounded-xl shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#0d9488]/10 border-2 border-[#0d9488]/20 flex items-center justify-center">
              <i className="fas fa-users-gear text-[#0d9488] text-sm" />
            </div>
            <div>
              <h3 className="font-syne text-sm font-extrabold text-[#0b1e33] uppercase tracking-tight">
                BULK ASSIGN / REASSIGN LEADS
              </h3>
              <p className="text-[10px] text-[#475569] font-mono font-bold">
                {selectedLeadIds.size > 0
                  ? `${getBulkAssignLeadIds().length} of ${selectedLeadIds.size} selected leads will be assigned`
                  : "Select status and assignee(s) to distribute leads equally"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-[9px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                LEAD STATUS
              </label>
              <select
                value={bulkAssignStatus}
                onChange={(e) => setBulkAssignStatus(e.target.value)}
                className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#0d9488] transition-all cursor-pointer"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === "new_lead"
                      ? "NEW LEAD"
                      : s.replace(/_/g, " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                HOW MANY LEADS TO ASSIGN
              </label>
              <input
                type="number"
                value={bulkAssignCount}
                onChange={(e) => setBulkAssignCount(e.target.value)}
                placeholder={`Max: ${getLeadsForBulkAssign().length}`}
                min="1"
                max={getLeadsForBulkAssign().length}
                className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#0d9488] transition-all placeholder:text-[#94a3b8]"
              />
              <div className="text-[8px] text-[#64748b] font-mono mt-1">
                Available:{" "}
                <span className="font-extrabold text-[#0d9488]">
                  {getLeadsForBulkAssign().length}
                </span>{" "}
                leads · Leave empty to assign all
              </div>
            </div>
            <div>
              <label className="text-[9px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                SELECT ASSIGNEE(S) ({selectedBulkStaff.length} selected)
              </label>
              <div className="max-h-[200px] overflow-y-auto bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl p-2 space-y-1">
                {conversionAssignable.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-[#0d9488]/5 transition-all"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBulkStaff.includes(s.id)}
                      onChange={() => toggleBulkStaff(s.id)}
                      className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#0d9488] focus:ring-[#0d9488] cursor-pointer"
                    />
                    <span className="text-[11px] font-extrabold text-[#0f172a]">
                      {s.firstName} {s.lastName}
                    </span>
                    <span className="text-[9px] text-[#64748b] font-mono">
                      ({getRoleLabel(s.role) || s.role})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleBulkAssign}
              disabled={bulkAssigning || selectedBulkStaff.length === 0}
              className="px-6 py-2.5 rounded-xl bg-[#0d9488] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0f766e] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[3px] border-[#0f766e] flex items-center gap-2"
            >
              {bulkAssigning ? (
                <>
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                  ASSIGNING...
                </>
              ) : (
                <>
                  <i className="fas fa-users-gear text-[9px]" /> ASSIGN{" "}
                  {getBulkAssignLeadIds().length} LEADS
                </>
              )}
            </button>
            <button
              onClick={() => {
                setShowBulkAssign(false);
                setSelectedBulkStaff([]);
              }}
              className="px-6 py-2.5 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {displayLeads.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-users text-[#94a3b8] text-3xl" />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">
            NO AnzoTrade LEADS
          </p>
          <p className="text-[#64748b] font-mono text-xs font-bold">
            Merge leads from the Lead Management (Staging) area to see them here
          </p>
          {(statusFilter !== "all" ||
            staffFilter !== "all" ||
            dateFilter ||
            depositFilter !== "all" ||
            searchResult !== null) && (
            <button
              onClick={() => {
                setStatusFilter("all");
                setStaffFilter("all");
                setDateFilter("");
                setDepositFilter("all");
                clearSearch();
              }}
              className="mt-2 text-[#0d9488] text-xs font-syne font-extrabold hover:underline uppercase tracking-wider"
            >
              CLEAR ALL FILTERS
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 px-5 py-2 bg-[#f8fafc] border-b-2 border-[#e2e8f0]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#0d9488] focus:ring-[#0d9488] cursor-pointer"
              />
              <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                Select All on Page
              </span>
            </label>
            {selectedCount > 0 && (
              <span className="text-[10px] text-[#0d9488] font-mono font-extrabold">
                ({selectedCount} selected total)
              </span>
            )}
          </div>

          <div className="divide-y-2 divide-[#f1f5f9]">
            {paginatedLeads.map((lead) => {
              const isRetentionLocked =
                lead.lead_type === "retention" &&
                (lead.assignedTo || lead.teamLeadId);
              const isChecked = selectedLeadIds.has(lead.id);
              const phones = lead.phones || [];

              return (
                <div
                  key={lead.id}
                  className={`p-4 transition-all ${isChecked ? "bg-[#f0fdfa] border-l-4 border-l-[#0d9488]" : "bg-white hover:bg-[#f8fafc]"}`}
                >
                  <div
                    className="flex items-start justify-between gap-4 cursor-pointer"
                    onClick={() =>
                      setExpandedLead(expandedLead === lead.id ? null : lead.id)
                    }
                  >
                    <div
                      className="flex items-center flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() =>
                          handleToggleLead(lead.id, isRetentionLocked)
                        }
                        disabled={isRetentionLocked}
                        className={`w-4 h-4 rounded border-2 cursor-pointer ${isRetentionLocked ? "border-[#fcd34d] bg-[#fef3c7] cursor-not-allowed" : "border-[#cbd5e1] text-[#0d9488] focus:ring-[#0d9488]"}`}
                        title={
                          isRetentionLocked
                            ? "Retention leads cannot be selected"
                            : "Select lead"
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-white border-2 border-[#e2e8f0] flex items-center justify-center flex-shrink-0 shadow-md">
                          <i className="fas fa-user text-sm text-black" />
                        </div>
                        <div>
                          <div className="font-syne text-sm font-extrabold text-[#0f172a] flex items-center gap-2">
                            {lead.name}
                            {lead.short_id && (
                              <span className="text-[10px] font-mono font-extrabold text-[#0d9488] bg-[#f0fdfa] border-2 border-[#99f6e4] px-2 py-0.5 rounded-lg">
                                #{lead.short_id}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-[#64748b] font-mono font-bold flex items-center gap-1 flex-wrap">
                            {canSeeFullDetails() ? (
                              <span>{lead.email}</span>
                            ) : (
                              <span>****@****</span>
                            )}
                            <span>·</span>
                            {canSeeFullDetails() ? (
                              <span>
                                {phones.map((p) => p.phone).join(" / ")}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => handleDialNumber(lead.id, e)}
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold"
                                title="Click to Call"
                              >
                                <i className="fas fa-phone-alt text-[9px]" />{" "}
                                CALL
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-[68px] text-[9px] flex-wrap mt-1.5">
                        {getStatusBadge(lead.status)}
                        {getGroxionBadge()}
                        {getLeadTypeBadge(lead.lead_type || "conversion")}
                        {getSourceBadge(lead.source)}
                        {getDepositBadge(lead)}
                        {lead.linked_user_id && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[9px] font-mono font-extrabold border-2 border-[#a7f3d0]">
                            <i className="fas fa-link mr-0.5 text-[8px]" />{" "}
                            USER: {lead.short_id}
                          </span>
                        )}
                        {lead.assignedFirstName && (
                          <span className="text-[#0d9488] font-extrabold">
                            <i className="fas fa-user-check mr-1" />
                            {lead.assignedFirstName} {lead.assignedLastName}
                          </span>
                        )}
                        {lead.teamLeadFirstName && (
                          <span className="text-[#1e3a5f] font-extrabold">
                            <i className="fas fa-user-tie mr-1" />
                            TL: {lead.teamLeadFirstName} {lead.teamLeadLastName}
                          </span>
                        )}
                        {phones.length > 1 && (
                          <span className="text-[#7c3aed] font-extrabold">
                            <i className="fas fa-phone mr-1" />
                            {phones.length} phones
                          </span>
                        )}
                        <span className="text-[#64748b] font-mono font-bold">
                          <i className="fas fa-calendar-plus mr-0.5 text-[8px]" />
                          {formatDate(lead.createdAt)}
                        </span>
                        {lead.closedAt && (
                          <span className="text-[#64748b] font-mono font-bold">
                            <i className="fas fa-calendar-check mr-0.5 text-[8px]" />
                            Closed: {formatDate(lead.closedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-2 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isRetentionLocked ? (
                        <div className="px-3 py-2 rounded-xl bg-[#fef3c7] border-2 border-[#fcd34d] text-[#92400e] text-[9px] font-mono font-extrabold flex items-center gap-1">
                          <i className="fas fa-lock text-[9px]" /> LOCKED
                          (RETENTION)
                        </div>
                      ) : (
                        <>
                          <select
                            value={
                              selectedStaff[lead.id] ||
                              lead.assignedTo ||
                              lead.teamLeadId ||
                              ""
                            }
                            onChange={(e) =>
                              setSelectedStaff({
                                ...selectedStaff,
                                [lead.id]: e.target.value,
                              })
                            }
                            className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] cursor-pointer"
                          >
                            <option value="">UNASSIGNED</option>
                            {conversionAssignable.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.firstName} {s.lastName}
                                {s.role !== "staff"
                                  ? ` (${getRoleLabel(s.role)})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                          {lead.assignedTo || lead.teamLeadId ? (
                            <button
                              onClick={() =>
                                handleTransfer(lead.id, selectedStaff[lead.id])
                              }
                              className="px-3 py-2 rounded-xl bg-[#0d9488] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0f766e] whitespace-nowrap border-b-[2px] border-[#0f766e]"
                            >
                              TRANSFER
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleAssign(lead.id, selectedStaff[lead.id])
                              }
                              className="px-3 py-2 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] whitespace-nowrap border-b-[2px] border-[#047857]"
                            >
                              ASSIGN
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(lead.id)}
                        className="px-3 py-2 rounded-xl bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] whitespace-nowrap border-b-[2px] border-[#b91c1c]"
                      >
                        DELETE
                      </button>
                    </div>
                  </div>

                  {expandedLead === lead.id && (
                    <div className="ml-[68px] mt-4 space-y-4 border-t-2 border-[#e2e8f0] pt-4">
                      <div className="flex flex-wrap gap-4 text-[10px] text-[#475569] font-mono font-bold">
                        <span>
                          <i className="fas fa-calendar-plus mr-1 text-[#0d9488]" />
                          Created: {formatDateTime(lead.createdAt)}
                        </span>
                        {lead.closedAt && (
                          <span>
                            <i className="fas fa-calendar-check mr-1 text-[#dc2626]" />
                            Closed: {formatDateTime(lead.closedAt)}
                          </span>
                        )}
                        {!lead.closedAt && (
                          <span>
                            <i className="fas fa-clock mr-1 text-[#d97706]" />
                            Open
                          </span>
                        )}
                        <span>
                          <i className="fas fa-tag mr-1 text-[#0d9488]" />
                          ID: {lead.short_id || "—"}
                        </span>
                        {lead.linked_user_id && (
                          <span>
                            <i className="fas fa-link mr-1 text-[#059669]" />
                            Linked: {lead.short_id}
                          </span>
                        )}
                        {lead.assignedFirstName && (
                          <span>
                            <i className="fas fa-user-check mr-1 text-[#0d9488]" />
                            Assigned: {lead.assignedFirstName}{" "}
                            {lead.assignedLastName}
                          </span>
                        )}
                        {lead.teamLeadFirstName && (
                          <span>
                            <i className="fas fa-user-tie mr-1 text-[#1e3a5f]" />
                            Team Lead: {lead.teamLeadFirstName}{" "}
                            {lead.teamLeadLastName}
                          </span>
                        )}
                        <span>
                          <i className="fas fa-envelope mr-1 text-[#0d9488]" />
                          Email:{" "}
                          {canSeeFullDetails() ? lead.email : "****@****"}
                        </span>
                        {lead.depositStatus === "deposited" &&
                          lead.depositDate && (
                            <span>
                              <i className="fas fa-coins mr-1 text-[#d97706]" />
                              Deposit:{" "}
                              {lead.depositAmount
                                ? `$${Number(lead.depositAmount).toFixed(0)}`
                                : ""}{" "}
                              on {formatDateTime(lead.depositDate)}
                            </span>
                          )}
                      </div>

                      <div className="p-4 rounded-xl bg-[#f8fafc] border-2 border-[#e2e8f0]">
                        <div className="text-[11px] text-[#475569] font-mono font-extrabold mb-3 flex items-center gap-2 uppercase tracking-wider">
                          <i className="fas fa-phone" /> PHONE NUMBERS (
                          {phones.length}/3)
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          {[0, 1, 2].map((slotIndex) => {
                            const phone = phones[slotIndex];
                            return (
                              <div
                                key={slotIndex}
                                className={`bg-white border-2 rounded-lg px-3 py-2.5 flex items-center justify-between min-h-[44px] ${phone ? "border-[#99f6e4]" : "border-dashed border-[#cbd5e1]"}`}
                              >
                                {phone ? (
                                  <div className="flex items-center justify-between w-full gap-2">
                                    <div className="min-w-0">
                                      {canSeeFullDetails() ? (
                                        <div className="text-[11px] font-mono font-extrabold text-[#0f172a] truncate">
                                          {phone.phone}
                                        </div>
                                      ) : (
                                        <button
                                          onClick={(e) =>
                                            handleDialNumber(lead.id, e)
                                          }
                                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold text-[10px]"
                                        >
                                          <i className="fas fa-phone-alt text-[9px]" />{" "}
                                          CALL
                                        </button>
                                      )}
                                      {phone.label && (
                                        <div className="text-[8px] text-[#64748b] font-mono mt-1">
                                          {phone.label}
                                        </div>
                                      )}
                                      {phone.isPrimary === 1 && (
                                        <span className="text-[7px] px-1.5 py-0.5 rounded bg-[#0d9488] text-white font-mono font-extrabold mt-1 inline-block">
                                          PRIMARY
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button
                                        onClick={() =>
                                          copyPhoneToClipboard(
                                            phone.phone,
                                            phone.id,
                                          )
                                        }
                                        className="w-6 h-6 rounded bg-[#f0fdfa] border border-[#99f6e4] text-[#0d9488] hover:bg-[#ccfbf1] flex items-center justify-center transition-all"
                                        title="Copy number"
                                      >
                                        <i
                                          className={`fas ${copiedPhone[phone.id] ? "fa-check text-[#059669]" : "fa-copy"} text-[9px]`}
                                        />
                                      </button>
                                      {phone.isPrimary !== 1 && (
                                        <button
                                          onClick={() =>
                                            handleSetPrimaryPhone(
                                              lead.id,
                                              phone.id,
                                            )
                                          }
                                          disabled={
                                            settingPrimaryPhone[phone.id]
                                          }
                                          className="w-6 h-6 rounded bg-[#eff6ff] border border-[#bfdbfe] text-[#1e3a5f] hover:bg-[#dbeafe] flex items-center justify-center transition-all disabled:opacity-40"
                                          title="Set as primary"
                                        >
                                          {settingPrimaryPhone[phone.id] ? (
                                            <span className="inline-block w-2 h-2 border border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
                                          ) : (
                                            <i className="fas fa-star text-[8px]" />
                                          )}
                                        </button>
                                      )}
                                      {phones.length > 1 && (
                                        <button
                                          onClick={() =>
                                            handleRemovePhone(lead.id, phone.id)
                                          }
                                          disabled={deletingPhone[phone.id]}
                                          className="w-6 h-6 rounded bg-[#fef2f2] border border-[#fecaca] text-[#dc2626] hover:bg-[#fee2e2] flex items-center justify-center transition-all disabled:opacity-40"
                                          title="Remove"
                                        >
                                          {deletingPhone[phone.id] ? (
                                            <span className="inline-block w-2 h-2 border border-[#dc2626] border-t-transparent rounded-full animate-spin" />
                                          ) : (
                                            <i className="fas fa-times text-[9px]" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-[#94a3b8] font-mono">
                                    Slot {slotIndex + 1} — Empty
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {phones.length < 3 && (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newPhoneInputs[lead.id] || ""}
                              onChange={(e) =>
                                setNewPhoneInputs({
                                  ...newPhoneInputs,
                                  [lead.id]: e.target.value,
                                })
                              }
                              placeholder="Phone number"
                              className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] placeholder:text-[#94a3b8]"
                            />
                            <input
                              type="text"
                              value={newPhoneLabels[lead.id] || ""}
                              onChange={(e) =>
                                setNewPhoneLabels({
                                  ...newPhoneLabels,
                                  [lead.id]: e.target.value,
                                })
                              }
                              placeholder="Label (optional)"
                              className="w-32 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] placeholder:text-[#94a3b8]"
                            />
                            <button
                              onClick={() => handleAddPhone(lead.id)}
                              disabled={
                                addingPhone[lead.id] ||
                                !newPhoneInputs[lead.id]?.trim()
                              }
                              className="px-4 py-2 rounded-lg bg-[#0d9488] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0f766e] disabled:opacity-50 transition-all border-b-[2px] border-[#0f766e] whitespace-nowrap"
                            >
                              {addingPhone[lead.id] ? (
                                <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <i className="fas fa-plus mr-1" /> ADD
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="p-4 rounded-xl bg-[#f8fafc] border-2 border-[#e2e8f0]">
                        <div className="text-[11px] text-[#475569] font-mono font-extrabold mb-3 flex items-center gap-2 uppercase tracking-wider">
                          <i className="fas fa-tag" /> LEAD TYPE
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              requestSetLeadType(lead, "conversion")
                            }
                            disabled={settingLeadType[lead.id]}
                            className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-b-[2px] ${(lead.lead_type || "conversion") === "conversion" ? "bg-[#059669] text-white border-[#047857]" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#059669] hover:text-[#059669]"}`}
                          >
                            <i className="fas fa-rotate mr-1.5" /> CONVERSION
                          </button>
                          <button
                            onClick={() =>
                              requestSetLeadType(lead, "retention")
                            }
                            disabled={settingLeadType[lead.id]}
                            className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-b-[2px] ${lead.lead_type === "retention" ? "bg-[#d97706] text-white border-[#b45309]" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#d97706] hover:text-[#d97706]"}`}
                          >
                            <i className="fas fa-lock mr-1.5" /> RETENTION
                          </button>
                          {settingLeadType[lead.id] && (
                            <span className="inline-block w-4 h-4 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
                          )}
                        </div>
                        {lead.lead_type === "retention" && (
                          <p className="text-[9px] text-[#92400e] font-mono font-bold mt-2 flex items-center gap-1">
                            <i className="fas fa-info-circle text-[9px]" /> This
                            lead is locked to{" "}
                            {lead.assignedFirstName
                              ? `${lead.assignedFirstName} ${lead.assignedLastName}`
                              : "its current assignee"}{" "}
                            and cannot be reassigned.
                          </p>
                        )}
                      </div>

                      <div className="p-4 rounded-xl bg-[#ecfdf5] border-2 border-[#a7f3d0]">
                        <div className="text-[11px] text-[#059669] font-mono font-extrabold mb-3 flex items-center gap-2 uppercase tracking-wider">
                          <i className="fas fa-user-plus" /> USER ACCOUNT
                        </div>
                        {!lead.linked_user_id ? (
                          <div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() =>
                                  setShowPasswordField({
                                    ...showPasswordField,
                                    [lead.id]: !showPasswordField[lead.id],
                                  })
                                }
                                className="px-4 py-2 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[2px] border-[#047857]"
                              >
                                <i className="fas fa-user-plus mr-1.5" /> CREATE
                                ACCOUNT
                              </button>
                              <span className="text-[10px] text-[#475569] font-mono font-bold">
                                Set a password to create the user account
                              </span>
                            </div>
                            {showPasswordField[lead.id] && (
                              <div className="flex items-center gap-2 mt-3">
                                <input
                                  type="text"
                                  value={passwordInputs[lead.id] || ""}
                                  onChange={(e) =>
                                    setPasswordInputs({
                                      ...passwordInputs,
                                      [lead.id]: e.target.value,
                                    })
                                  }
                                  placeholder="Set password (min 6 chars)"
                                  className="bg-white border-2 border-[#a7f3d0] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#059669] w-56"
                                />
                                <button
                                  onClick={() => handleConvertToUser(lead.id)}
                                  disabled={converting[lead.id]}
                                  className="px-4 py-2.5 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] disabled:opacity-50 transition-all border-b-[2px] border-[#047857]"
                                >
                                  {converting[lead.id] ? (
                                    <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    "CONFIRM & CREATE"
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => handleViewLinkedUser(lead.id)}
                              className="px-4 py-2 rounded-xl bg-[#0d9488] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0f766e] transition-all border-b-[2px] border-[#0f766e]"
                            >
                              <i className="fas fa-eye mr-1.5" /> VIEW USER
                              DETAILS
                            </button>
                            <button
                              onClick={() =>
                                setShowPasswordField({
                                  ...showPasswordField,
                                  [lead.id]: !showPasswordField[lead.id],
                                })
                              }
                              className="px-4 py-2 rounded-xl bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b45309] transition-all border-b-[2px] border-[#b45309]"
                            >
                              <i className="fas fa-key mr-1.5" /> RESET PASSWORD
                            </button>
                            <button
                              onClick={() =>
                                window.open(
                                  `/admin/user/${lead.linked_user_id}`,
                                  "_blank",
                                )
                              }
                              className="px-4 py-2 rounded-xl bg-[#0d9488] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0f766e] transition-all border-b-[2px] border-[#0f766e]"
                            >
                              <i className="fas fa-external-link-alt mr-1.5" />{" "}
                              GO TO USER PAGE
                            </button>
                            {showPasswordField[lead.id] && (
                              <div className="flex items-center gap-2 w-full mt-2">
                                <input
                                  type="text"
                                  value={passwordInputs[lead.id] || ""}
                                  onChange={(e) =>
                                    setPasswordInputs({
                                      ...passwordInputs,
                                      [lead.id]: e.target.value,
                                    })
                                  }
                                  placeholder="New password (min 6 chars)"
                                  className="bg-white border-2 border-[#fde047] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#d97706] w-56"
                                />
                                <button
                                  onClick={() => handleResetPassword(lead.id)}
                                  disabled={converting[lead.id]}
                                  className="px-4 py-2.5 rounded-xl bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b45309] disabled:opacity-50 transition-all border-b-[2px] border-[#b45309]"
                                >
                                  {converting[lead.id] ? (
                                    <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    "UPDATE PASSWORD"
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {lead.comments && lead.comments.length > 0 && (
                        <div className="space-y-2 mb-2">
                          <div className="text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider flex items-center gap-2">
                            COMMENTS HISTORY{" "}
                            <span className="text-[#0d9488] text-[8px] bg-[#f0fdfa] px-2 py-0.5 rounded border border-[#99f6e4]">
                              REAL - INTERNAL
                            </span>
                          </div>
                          {lead.comments.map((c) => (
                            <div
                              key={c.id}
                              className="flex items-start gap-2 text-[10px] bg-[#f8fafc] rounded-lg p-2.5 border border-[#e2e8f0]"
                            >
                              <span className="text-[#0d9488] font-extrabold whitespace-nowrap">
                                {c.staffName}:
                              </span>
                              <span className="text-[#475569] font-bold">
                                {c.comment}
                              </span>
                              <span className="text-[#94a3b8] ml-auto whitespace-nowrap font-mono text-[9px]">
                                {formatDateTime(c.createdAt)}
                              </span>
                              {c.status && (
                                <span className="text-[#64748b] whitespace-nowrap font-bold ml-1">
                                  → {c.status.replace(/_/g, " ").toUpperCase()}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-start gap-2">
                        <select
                          value={statusInputs[lead.id] || ""}
                          onChange={(e) => {
                            setStatusInputs({
                              ...statusInputs,
                              [lead.id]: e.target.value,
                            });
                            setCustomStatus({ ...customStatus, [lead.id]: "" });
                          }}
                          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] w-[150px] flex-shrink-0 cursor-pointer"
                        >
                          <option value="">SELECT STATUS...</option>
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s === "new_lead"
                                ? "NEW LEAD"
                                : s.replace(/_/g, " ").toUpperCase()}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={customStatus[lead.id] || ""}
                          onChange={(e) => {
                            setCustomStatus({
                              ...customStatus,
                              [lead.id]: e.target.value,
                            });
                            setStatusInputs({ ...statusInputs, [lead.id]: "" });
                          }}
                          placeholder="Or write custom..."
                          className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] placeholder:text-[#94a3b8]"
                        />
                        <textarea
                          value={commentInputs[lead.id] || ""}
                          onChange={(e) =>
                            setCommentInputs({
                              ...commentInputs,
                              [lead.id]: e.target.value,
                            })
                          }
                          placeholder="Add real comment..."
                          rows={1}
                          className="flex-[2] bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#0d9488] resize-none placeholder:text-[#94a3b8]"
                        />
                        <button
                          onClick={() => handleUpdate(lead.id)}
                          disabled={updating[lead.id]}
                          className="px-5 py-2.5 rounded-xl bg-[#0d9488] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0f766e] disabled:opacity-50 transition-all whitespace-nowrap border-b-[3px] border-[#0f766e]"
                        >
                          {updating[lead.id] ? (
                            <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            "UPDATE"
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Pagination />
        </>
      )}

      {showUserModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          leadInfo={leads.find((l) => l.linked_user_id === selectedUser.id)}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {confirmMove && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            if (!confirmLoading) setConfirmMove(null);
          }}
        >
          <div
            className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-[440px] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b-2 border-[#e2e8f0] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-md ${confirmMove.targetType === "retention" ? "bg-gradient-to-br from-[#d97706] to-[#b45309] shadow-[#d97706]/25" : "bg-gradient-to-br from-[#059669] to-[#047857] shadow-[#059669]/25"}`}
                >
                  <i
                    className={`text-white text-sm ${confirmMove.targetType === "retention" ? "fas fa-lock" : "fas fa-rotate"}`}
                  />
                </div>
                <div>
                  <h3 className="font-syne text-base font-extrabold text-[#0b1e33] uppercase tracking-tight">
                    {confirmMove.targetType === "retention"
                      ? "Move to Retention"
                      : "Move to Conversion"}
                  </h3>
                  <p className="text-[10px] text-[#475569] font-mono font-bold">
                    {confirmMove.targetType === "retention"
                      ? "This action locks the lead"
                      : "This action unlocks the lead"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (!confirmLoading) setConfirmMove(null);
                }}
                disabled={confirmLoading}
                className="w-9 h-9 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#dc2626] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-all flex items-center justify-center disabled:opacity-40"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-[12px] text-[#0f172a] font-bold leading-relaxed">
                {confirmMove.targetType === "retention"
                  ? "Are you sure you want to move this lead to Retention? It will be removed from this page and locked to its current assignee."
                  : "Are you sure you want to move this lead back to Conversion? It will return to the AnzoTrade Leads page and become assignable again."}
              </p>
            </div>
            <div className="px-6 py-4 bg-[#f8fafc] border-t-2 border-[#e2e8f0] flex gap-3">
              <button
                onClick={() => setConfirmMove(null)}
                disabled={confirmLoading}
                className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] disabled:opacity-50 transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={confirmMoveLead}
                disabled={confirmLoading}
                className={`flex-1 py-3 rounded-xl text-white text-[10px] font-syne font-extrabold uppercase tracking-wider transition-all border-b-[3px] flex items-center justify-center gap-2 ${confirmMove.targetType === "retention" ? "bg-[#d97706] hover:bg-[#b45309] border-[#b45309]" : "bg-[#059669] hover:bg-[#047857] border-[#047857]"} disabled:opacity-50`}
              >
                {confirmLoading ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    MOVING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check text-[9px]" />
                    {confirmMove.targetType === "retention"
                      ? "YES, MOVE TO RETENTION"
                      : "YES, MOVE TO CONVERSION"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── RECENT CALLS BUTTON & POPUP ─── */}
      <button onClick={() => setShowCallHistory(true)} className="fixed bottom-6 right-6 z-[9990] w-14 h-14 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center shadow-xl hover:bg-[#0b1e33] transition-all border-b-4 border-[#0b1e33]" title="Recent Calls">
        <i className="fas fa-history text-xl" />
      </button>

      {showCallHistory && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCallHistory(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border-2 border-[#e2e8f0] animate-bounce-in" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 bg-[#0b1e33] text-white flex items-center justify-between">
              <div>
                <h3 className="font-syne text-lg font-extrabold uppercase tracking-tight">Recent Incoming Calls</h3>
                <p className="text-[10px] font-mono text-[#bfdbfe] mt-0.5">No numbers shown for privacy</p>
              </div>
              <button onClick={() => setShowCallHistory(false)} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"><i className="fas fa-times text-sm" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {callHistory.length === 0 ? (
                <div className="text-center py-12 text-[#64748b]">
                  <i className="fas fa-phone-slash text-3xl mb-3 opacity-30" />
                  <p className="font-syne font-extrabold uppercase tracking-wider">No recent calls</p>
                </div>
              ) : (
                <div className="divide-y divide-[#f1f5f9]">
                  {callHistory.map((call) => (
                    <div key={call.id} className="p-4 flex items-center justify-between hover:bg-[#f8fafc] transition-all">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${call.shortId ? 'bg-[#eff6ff] text-[#1e3a5f]' : 'bg-[#f1f5f9] text-[#94a3b8]'}`}>
                          <i className={`fas ${call.shortId ? 'fa-user' : 'fa-phone-slash'} text-sm`} />
                        </div>
                        <div>
                          <div className="font-syne text-sm font-extrabold text-[#0f172a]">{call.leadName}</div>
                          <div className="text-[10px] font-mono font-bold text-[#64748b] flex items-center gap-2 mt-0.5">
                            <span>{call.timestamp}</span>
                            {call.shortId && <span className="text-[#059669] bg-[#ecfdf5] px-1.5 py-0.5 rounded border border-[#a7f3d0]">#{call.shortId}</span>}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span className={`text-[9px] font-mono font-extrabold uppercase px-2 py-1 rounded-lg ${call.shortId ? 'bg-[#ecfdf5] text-[#059669]' : 'bg-[#fef2f2] text-[#dc2626]'}`}>
                          {call.shortId ? 'Matched' : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 bg-[#f8fafc] border-t-2 border-[#e2e8f0] flex justify-end">
              <button onClick={() => setShowCallHistory(false)} className="px-6 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM INCOMING CALL WIDGET (no number shown) */}
      {incomingCall && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 10000 }} className="w-[320px] bg-white border-2 border-[#1e3a5f] rounded-2xl shadow-2xl overflow-hidden animate-bounce-in">
          <div className="px-4 py-3 bg-[#0b1e33] text-white">
            <div className="text-[9px] font-mono font-extrabold uppercase tracking-wider text-[#a7f3d0] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a7f3d0] animate-pulse" /> INCOMING CALL
            </div>
            <div className="font-syne text-base font-extrabold mt-1 truncate">{incomingCall.lead?.name || "Unknown caller"}</div>
            {incomingCall.lead?.short_id && <div className="text-[10px] font-mono font-bold text-[#bfdbfe]">#{incomingCall.lead.short_id}</div>}
          </div>
          <div className="flex gap-2 p-3">
            <button onClick={declineCall} className="flex-1 py-2.5 rounded-xl bg-[#dc2626] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] border-b-[3px] border-[#b91c1c]"><i className="fas fa-phone-slash mr-1.5" /> DECLINE</button>
            <button onClick={acceptCall} className="flex-1 py-2.5 rounded-xl bg-[#059669] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] border-b-[3px] border-[#047857]"><i className="fas fa-phone-alt mr-1.5" /> ACCEPT</button>
          </div>
        </div>
      )}

      {/* CUSTOM END CALL BUTTON (outgoing calls) */}
      {isCallActive && !incomingCall && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
          <button onClick={handleHangup} className="px-6 py-3 rounded-full bg-[#dc2626] text-white font-syne font-extrabold uppercase tracking-wider shadow-2xl hover:bg-[#b91c1c] transition-all border-b-[3px] border-[#b91c1c] flex items-center gap-2">
            <i className="fas fa-phone-slash text-lg" /> END CALL
          </button>
        </div>
      )}
    </div>
  );
};

export default GroxionLead;